import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Users,
  Building2,
  AlertTriangle,
  Info,
} from "lucide-react";
import {
  useGetDepartmentsByBusinessUnit,
  useGetAcceptedbycode,
  getVacancies,
  useGetBusinessUnits,
} from "../../adapters/Requests";
import SearchableSelect from "../ui/SearchableSelect";

const getRawApplication = (item) => {
  if (!item) return null;
  if (item.original) {
    if (item.original.original) {
      return item.original.original;
    }
    return item.original;
  }
  return item;
};

export default function BulkAcceptModal({
  isOpen,
  onClose,
  selectedApplicants = [],
  onConfirm,
  isPending,
  result,
}) {
  const [department, setDepartment] = useState("");
  const [childBusinessUnitId, setChildBusinessUnitId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const rawApplication = getRawApplication(selectedApplicants[0]);
  const embeddedVacancy = rawApplication?.vacancy;
  const vacancyId = embeddedVacancy?.id || rawApplication?.vacancy_id;
  const shouldFetchVacancies =
    isOpen &&
    vacancyId &&
    (!embeddedVacancy?.quota || !embeddedVacancy?.business_unit);

  const { data: allVacancies, isLoading: isLoadingVacancies } = getVacancies({
    enabled: !!shouldFetchVacancies,
  });
  const vacancy =
    embeddedVacancy?.quota && embeddedVacancy?.business_unit
      ? embeddedVacancy
      : allVacancies?.find((v) => v.id === vacancyId) || {};

  const businessUnit = vacancy?.business_unit;
  const { data: childBusinessUnits } = useGetBusinessUnits(businessUnit?.id);
  const calculatedChildBUs = React.useMemo(() => {
    if (!businessUnit) return [];
    const childrenFromList = childBusinessUnits || [];
    if (childrenFromList.length > 0) return childrenFromList;
    return businessUnit.children || [];
  }, [childBusinessUnits, businessUnit]);

  const buIdToFetch = childBusinessUnitId || businessUnit?.id;

  const { data: departmentResponse, isLoading: isLoadingDepts } =
    useGetDepartmentsByBusinessUnit(
      { business_unit_id: buIdToFetch },
      { enabled: !!buIdToFetch && isOpen },
    );

  const { data: acceptedData, isLoading: isLoadingAccepted } =
    useGetAcceptedbycode(vacancyId);
  useEffect(() => {
    if (isOpen && departmentResponse?.departments) {
      setDepartments(departmentResponse.departments);
    } else {
      setDepartments([]);
    }
  }, [departmentResponse, isOpen, buIdToFetch]);

  useEffect(() => {
    if (!isOpen) {
      setDepartment("");
      setChildBusinessUnitId(null);
      setDepartments([]);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (department) onConfirm(department);
  };

  const childBuOptions = calculatedChildBUs.map((bu) => ({
    value: bu.id,
    label: bu.name,
  }));

  const deptOptions = departments.map((dept) => ({
    value: dept.code,
    label: dept.name,
  }));
  const selectedDepartmentName =
    deptOptions.find((dept) => dept.value === department)?.label || department;

  const quota = vacancy?.quota || 0;
  const acceptedCount = acceptedData?.count || 0;
  const remainingQuota = Math.max(0, quota - acceptedCount);
  const selectedCount = selectedApplicants.length;
  const remainingAfterAcceptance = remainingQuota - selectedCount;
  const isQuotaExceeded = selectedCount > remainingQuota;

  const showSbuDropdown = calculatedChildBUs.length > 0;
  const showDeptDropdown =
    childBusinessUnitId || (!showSbuDropdown && businessUnit);

  const isInitializing =
    (isLoadingVacancies && shouldFetchVacancies) || (!businessUnit && isOpen);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Users className="h-6 w-6 text-green-600" />
            Bulk Acceptance
          </DialogTitle>
        </DialogHeader>

        {!result && !isPending && (
          <div className="py-4 space-y-5">
            {isInitializing ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm space-y-2">
                  <div className="flex items-center gap-2 font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-2">
                    <Info className="h-4 w-4" /> Vacancy Statistics
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Total Quota:</span>
                      <div className="font-bold text-gray-800">{quota}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Already Accepted:</span>
                      <div className="font-bold text-gray-800">
                        {isLoadingAccepted ? "..." : acceptedCount}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Remaining Slots:</span>
                      <div className="font-bold text-gray-800">
                        {isLoadingAccepted ? "..." : remainingQuota}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Selected:</span>
                      <div className="font-bold text-green-600">
                        {selectedCount}
                      </div>
                    </div>
                  </div>

                  {!isLoadingAccepted && (
                    <div
                      className={`mt-2 pt-2 border-t ${
                        isQuotaExceeded
                          ? "border-red-200 text-red-700"
                          : "border-gray-200 text-green-700"
                      }`}
                    >
                      <div className="flex justify-between items-center font-bold">
                        <span>Balance After Action:</span>
                        <span>{remainingAfterAcceptance}</span>
                      </div>
                    </div>
                  )}
                </div>

                {isQuotaExceeded && (
                  <div className="flex items-start gap-2 bg-red-50 p-3 rounded-md border border-red-100 text-xs text-red-600">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p>
                      <strong>Warning:</strong> The number of selected
                      applicants ({selectedCount}) exceeds the remaining quota (
                      {remainingQuota}).
                    </p>
                  </div>
                )}

                {showSbuDropdown && (
                  <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1">
                    <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> Business / Strategic
                      Unit
                    </label>
                    <SearchableSelect
                      title="Business Unit"
                      options={childBuOptions}
                      value={childBusinessUnitId}
                      onChange={(val) => {
                        setChildBusinessUnitId(val);
                        setDepartment("");
                      }}
                    />
                  </div>
                )}
                {showDeptDropdown && (
                  <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-300">
                    <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> Department
                    </label>

                    {isLoadingDepts ? (
                      <div className="flex items-center gap-2 text-xs text-gray-500 h-10 px-3 border rounded-md bg-gray-50">
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading
                        departments...
                      </div>
                    ) : (
                      <SearchableSelect
                        title="Department"
                        options={deptOptions}
                        value={department}
                        onChange={setDepartment}
                        disabled={deptOptions.length === 0}
                      />
                    )}

                    {deptOptions.length === 0 && !isLoadingDepts && (
                      <p className="text-[10px] text-orange-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> No departments
                        found in this unit.
                      </p>
                    )}
                  </div>
                )}

                <div className="bg-green-50 border border-green-100 rounded-md p-3 text-sm text-green-700">
                  You are accepting <strong>{selectedCount}</strong> applicants.
                  <br />
                  <span className="text-xs mt-1 block opacity-80">
                    Placement Location:{" "}
                    <strong>{businessUnit?.name || "Loading..."}</strong>
                  </span>
                  {department && (
                    <p className="text-xs pt-2 font-semibold animate-in fade-in">
                      Destination: {selectedDepartmentName}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {isPending && (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-green-600" />
            <p className="text-sm font-medium text-gray-600">
              Processing Applications...
            </p>
          </div>
        )}

        {result && (
          <div className="py-4 space-y-4">
            {result.successfulCount > 0 && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-md flex items-center gap-3">
                <CheckCircle className="text-green-600 h-6 w-6" />
                <div>
                  <h4 className="font-bold text-green-800 text-sm">Success</h4>
                  <p className="text-xs text-green-700">
                    {result.successfulCount} applicants accepted.
                  </p>
                </div>
              </div>
            )}

            {result.failedCount > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md overflow-hidden">
                <div className="px-4 py-2 bg-red-100/50 border-b border-red-200 flex justify-between items-center">
                  <h4 className="font-bold text-red-800 text-xs uppercase flex items-center gap-2">
                    <XCircle className="h-4 w-4" /> Failures (
                    {result.failedCount})
                  </h4>
                </div>
                {result.errors && (
                  <ul className="p-2 max-h-32 overflow-y-auto space-y-2">
                    {result.errors.map((err, idx) => (
                      <li
                        key={idx}
                        className="text-xs text-red-700 flex gap-2 items-start"
                      >
                        <span className="shrink-0">•</span>
                        <span>
                          {typeof err === "string" ? err : JSON.stringify(err)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result && !isPending && (
            <>
              <Button variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleConfirm}
                disabled={
                  !department ||
                  (showSbuDropdown && !childBusinessUnitId) ||
                  isInitializing
                }
                className={`${
                  isQuotaExceeded
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                } text-white`}
              >
                {isQuotaExceeded ? "Confirm Anyway" : "Confirm Acceptance"}
              </Button>
            </>
          )}
          {result && (
            <Button size="sm" variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
